import {isFeatureEnabled} from '@github-ui/feature-flags'
import {HeaderAgentTasks} from '@github-ui/issue-agent-tasks/HeaderAgentTasks'
import {Suspense} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {OptionConfig} from '../OptionConfig'
import type {HeaderBlockedBySummary$key} from './__generated__/HeaderBlockedBySummary.graphql'
import type {HeaderSubIssueSummary$key} from './__generated__/HeaderSubIssueSummary.graphql'
import type {LinkedSecurityAlertsFragment$key} from './__generated__/LinkedSecurityAlertsFragment.graphql'
import type {StickyHeaderChips$key} from './__generated__/StickyHeaderChips.graphql'
import type {TrackedByFragment$key} from './__generated__/TrackedByFragment.graphql'
import {HeaderBlockedBySummary} from './HeaderBlockedBySummary'
import {SmallHeaderIssueType} from './HeaderIssueType'
import {HeaderSubIssueSummary} from './HeaderSubIssueSummary'
import {LinkedSecurityAlerts} from './LinkedSecurityAlerts'
import {SmallRepositoryPill} from './RepositoryPill'
import styles from './StickyHeaderChips.module.css'
import {SmallTrackedBy} from './TrackedBy'

export type StickyHeaderChipsProps = {
  issueKey: StickyHeaderChips$key
  secondaryIssueDataKey?:
    | TrackedByFragment$key
    | HeaderBlockedBySummary$key
    | HeaderSubIssueSummary$key
    | LinkedSecurityAlertsFragment$key
  optionConfig: OptionConfig
}

export function StickyHeaderChips({issueKey, secondaryIssueDataKey, optionConfig}: StickyHeaderChipsProps) {
  const showAgentTasksPanel = isFeatureEnabled('issue_cca_visualization_session_panel')
  const {showRepositoryPill} = optionConfig

  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment StickyHeaderChips on Issue {
        url
        repository {
          nameWithOwner
        }
        ...HeaderIssueType
        ...HeaderAgentTasks
        ...HeaderSubIssueSummaryWithPrimary
        ...RepositoryPill
      }
    `,
    issueKey,
  )

  return (
    <div className={styles.chipsRow}>
      <SmallHeaderIssueType data={data} />
      {showAgentTasksPanel && <HeaderAgentTasks issueData={data} isSticky />}
      <HeaderSubIssueSummary
        subIssuePrimaryKey={data}
        subIssueSecondaryKey={secondaryIssueDataKey as HeaderSubIssueSummary$key | undefined}
        size="small"
      />
      {secondaryIssueDataKey && (
        <>
          <HeaderBlockedBySummary
            blockedBySecondaryKey={secondaryIssueDataKey as HeaderBlockedBySummary$key}
            size="small"
          />
          <LinkedSecurityAlerts issueKey={secondaryIssueDataKey as LinkedSecurityAlertsFragment$key} isSmall />
          <Suspense>
            <SmallTrackedBy url={data.url} trackedByKey={secondaryIssueDataKey as TrackedByFragment$key} />
          </Suspense>
        </>
      )}
      {showRepositoryPill && <SmallRepositoryPill repositoryPillData={data} />}
    </div>
  )
}
